import React, { useCallback, useEffect, useRef, FC } from 'react'
import { isEqual } from 'date-fns'

import { toastr } from 'components/Toastr'
import { useAuth } from 'providers/Auth'
import { useAbly } from 'providers/Ably'

import { fetchPhoneNumber } from 'services/api/core/applications/requester'

import { getNotificationsToShow } from './helpers/getNotificationsToShow'
import { mountAblyPayload } from './helpers/mountAblyPayload'
import { useSchedulingNotification } from './hooks/useSchedulingNotification'
import { usePersistCloseToastr } from './hooks/usePersistCloseToastr'
import { Message } from './Message'

interface Assignment {
  id: string
  scheduling: Date
  name: string
}

const NextScheduledContact: FC<{
  workflows?: Workflow[]
  threshold?: number
}> = ({ workflows = [], threshold = 5 }) => {
  const [closedToastr, setClosedToastr] = usePersistCloseToastr()
  const visibleIds = useRef([] as string[])
  const socket = useAbly()
  const user = useAuth()

  const callAction = useCallback(
    async (applicationId, onStart, onFinish) => {
      onStart()
      const phoneNumber = await fetchPhoneNumber(applicationId)
      const payload = mountAblyPayload(
        applicationId,
        Number(user.extensionNumber),
        phoneNumber,
      )
      const channel = socket.channels.get(`webphone-${user.id}`)
      channel.publish('call_requested', payload)
      onFinish()
    },
    [user, socket],
  )

  const trasformedAssignments: Assignment[] = workflows
    .filter(assignment => assignment.scheduling)
    .map(
      (assignment: Workflow): Assignment => ({
        id: assignment.id,
        name: assignment.fullName,
        scheduling: new Date(assignment.scheduling),
      }),
    )

  const notifications = useSchedulingNotification(
    trasformedAssignments,
    threshold,
  ).filter(
    notification =>
      !closedToastr[notification.id] ||
      !isEqual(
        notification.scheduling,
        new Date(closedToastr[notification.id]),
      ),
  )

  useEffect(() => {
    const notificationsToShow = getNotificationsToShow(
      notifications,
      visibleIds.current,
    )

    visibleIds.current = [
      ...visibleIds.current,
      ...notificationsToShow.map(notification => notification.id),
    ]

    notificationsToShow.forEach(notification => {
      toastr.info(
        <Message
          id={notification.id}
          name={notification.name}
          onCall={callAction}
          scheduling={notification.scheduling}
        />,
        {
          onClose: () => {
            setClosedToastr.current(
              notification.id,
              notification.scheduling.toISOString(),
            )
          },
        },
      )
    })
  }, [notifications, callAction, closedToastr, setClosedToastr])

  return <></>
}

export { NextScheduledContact }
